import { gsap } from 'gsap';
import { Draggable } from 'gsap/Draggable';
import { InertiaPlugin } from 'gsap/InertiaPlugin';
import throttle from '../utilities/throttle';
import debounce from '../utilities/debounce';

gsap.registerPlugin(Draggable);
gsap.registerPlugin(InertiaPlugin);

const setup = () => {
  const intro = document.querySelector('[data-w-intro]');
  if (!intro) return;

  const slider = intro.querySelector('[data-w-intro-slides]');
  const slides = intro.querySelectorAll('[data-w-intro-slide]');
  const infos = intro.querySelectorAll('[data-w-intro-slide-info]');
  // const pagination = document.querySelector('[data-w-intro-number]');

  // const prevButton = document.querySelector('[data-w-slider-control="prev"]');
  // const nextButton = document.querySelector('[data-w-slider-control="next"]');

  // let offsetX = 0;
  // let offsetY = 0;
  // document.addEventListener('mousemove', (e) => {
  //   offsetX = -0.025 * (e.clientX - window.innerWidth * 0.5);
  //   offsetY = -0.0125 * (e.clientY - window.innerHeight * 0.5);

  //   slider.style.setProperty('--_offset-x', `${offsetX}px`);
  //   slider.style.setProperty('--_offset-y', `${offsetY}px`);
  // });

  let autoplayDirection = 1;
  const slideDuration = 0.5;
  const slideDelay = slideDuration * 4;
  const numSlides = slides.length;
  const progressPerItem = 1 / (numSlides - 1);
  const snapProgress = gsap.utils.snap(progressPerItem);
  const snapProgressDirectional = (value, direction) => {
    let snapped = snapProgress(value);
    return snapped - value < 0 === direction < 0
      ? snapped
      : snapProgress(
          direction < 0 ? value - progressPerItem : value + progressPerItem
        );
  };
  const timer = gsap.delayedCall(slideDelay, autoPlay);
  let slideAnimation; // as reference
  let slideHeight;
  let totalHeight;

  // const updateButtonStatus = (progress) => {
  //   pagination.innerText = (numSlides - 1) * progress + 1;
  // };

  let animation;
  let animationCreated = false;

  const createTimeline = () => {
    animation = gsap.timeline({
      paused: true,
      // onUpdate: () => {
      //   // console.log(animation.progress().toFixed(3));
      //   // pagination.innerText = `${parseFloat(animation.progress()).toFixed(3)}`;
      // },
    });
    slides.forEach((slide, index) => {
      const relatedInfo = infos[index];
      const media = slide.querySelector('[data-w-intro-slide-media]');
      const video = slide.querySelector('video');

      gsap.set(slide, {
        '--slideoffset': 0,
        zIndex: 1 - index,
      });

      if (index > 0) {
        gsap.set(slide, {
          y: '-25%',
          scale: 0.625,
        });

        animation.to(
          slide,
          {
            y: '0%',
            scale: 1,
            duration: progressPerItem,
            onStart: () => {
              if (video) video.play();
            },
          },
          progressPerItem * index - progressPerItem
        );

        gsap.set(relatedInfo, {
          opacity: 0,
        });
        animation.to(
          relatedInfo,
          {
            opacity: 1,
            duration: progressPerItem,
          },
          progressPerItem * index - progressPerItem
        );
      }

      if (index > 1) {
        gsap.set(media, {
          scale: 0.75,
          opacity: 0,
        });

        animation.to(
          media,
          {
            scale: 1,
            opacity: 1,
            duration: progressPerItem,
          },
          progressPerItem * index - progressPerItem * 2
        );
      }

      if (index < numSlides - 1) {
        animation.to(
          slide,
          {
            y: '50vh', // () => window.innerHeight * 0.5 + media.clientHeight * 0.5 - 64,
            '--slideoffset': 1,
            duration: progressPerItem,
          },
          progressPerItem * index
        );

        animation.to(
          media,
          {
            y: '50%',
            scale: 1.5,
            duration: progressPerItem,
          },
          progressPerItem * index
        );

        animation.to(
          relatedInfo,
          {
            opacity: 0,
            duration: progressPerItem,
          },
          progressPerItem * index
        );
      }

      if (index < numSlides - 2) {
        animation.to(
          media,
          {
            y: '100%',
            opacity: 0,
            duration: progressPerItem,
          },
          progressPerItem * index + progressPerItem
        );
      }
    });
    animationCreated = true;
  };
  createTimeline();

  // // @debug timeline
  // animation.getChildren().forEach((child) => {
  //   console.log(child.startTime(), child.duration());
  // });

  const tracker = InertiaPlugin.track(animation, 'progress')[0]; // tracks velocity of progress
  const proxy = document.createElement('div'); // use a proxy element
  const draggable = new Draggable(proxy, {
    type: 'y',
    trigger: slider,
    allowContextMenu: true,
    onPress() {
      gsap.killTweensOf(animation);
      this.startProgress = animation.progress();
    },
    onDrag() {
      let change = (draggable.startY - draggable.y) / totalHeight;
      const progress = draggable.startProgress + change;
      animation.progress(progress);

      // updateButtonStatus(snapProgress(progress));
    },
    onRelease() {
      let velocity = tracker.get('progress');

      gsap.to(animation, {
        inertia: {
          duration: { min: slideDuration, max: slideDuration * 2 },
          progress: {
            velocity: velocity,
            min: -1,
            max: 2,
            end: (value) => snapProgressDirectional(value, velocity),
          },
        },
        // onUpdate: throttle(() => {
        //   const progress = snapProgress(animation.progress());
        //   updateButtonStatus(progress);
        // }, 250),
        onComplete: function () {
          // const progress = snapProgress(animation.progress());
          // updateButtonStatus(progress);

          timer.restart(true);
        },
        overwrite: true,
      });
    },
  });

  const animateSlides = (direction) => {
    let progress = snapProgress(
      animation.progress() + direction * progressPerItem
    );

    if (progress === 0 || progress === 1) {
      autoplayDirection *= -1;
    }

    if (progress >= 0 && progress <= 1) {
      // updateButtonStatus(progress);

      slideAnimation = gsap.to(animation, {
        progress: progress,
        duration: slideDuration * 8,
        ease: 'power3.inOut',
        overwrite: true,
        onComplete: function () {
          // const progress = snapProgress(animation.progress());
          // updateButtonStatus(progress);

          timer.restart(true);
        },
      });
    }
  };

  function autoPlay() {
    if (draggable.isPressed || gsap.isTweening(animation)) {
      timer.restart(true);
    } else {
      animateSlides(autoplayDirection);
    }
  }

  function resize() {
    slideHeight = window.innerHeight;
    totalHeight = slideHeight * numSlides;
  }
  resize();
  window.addEventListener('resize', throttle(resize, 100));

  // prevButton.addEventListener('click', () => {
  //   animateSlides(-1);
  // });
  // nextButton.addEventListener('click', () => {
  //   animateSlides(1);
  // });

  // updateButtonStatus(0); // init button status
};

export default function Intro() {
  return {
    init: () => {
      setup();
    },
  };
}
